import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import api from '../services/api';
import './DocumentHistory.css';

const DocumentHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  // Fermer le menu quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await api.get('/documents/');
      setDocuments(response.data.results || response.data);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMenu = (documentId) => {
    setOpenMenuId(openMenuId === documentId ? null : documentId);
  };

  const handleEdit = (documentId) => {
    setOpenMenuId(null);
    navigate(`/document/edit/${documentId}`);
  };

  const handleDelete = async (documentId) => {
    setOpenMenuId(null);
    
    if (!window.confirm(t('documentHistory.confirmDelete') || 'Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    setDeleting(documentId);
    try {
      await api.delete(`/documents/${documentId}/`);
      setDocuments(documents.filter(d => d.id !== documentId));
    } catch (error) {
      console.error('Error deleting document:', error);
      alert(t('documentHistory.deleteError') || 'Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getPreview = (content) => {
    const text = stripHtml(content);
    return text.length > 150 ? text.substring(0, 150) + '...' : text;
  };

  if (loading) {
    return <div className="document-history-loading">Chargement...</div>;
  }

  return (
    <div className="document-history">
      <Navbar />

      <div className="document-history-content">
        {documents.length === 0 ? (
          <div className="no-documents">
            <p>Aucun document trouvé. Créez votre premier document !</p>
          </div>
        ) : (
          <div className="documents-list">
            {documents.map((document) => (
              <div key={document.id} className="document-card">
                <div className="document-header">
                  <div className="document-header-left">
                    <h3>{document.titre || `Document #${document.id}`}</h3>
                  </div>
                  <div className="document-header-right">
                    <span className="document-date">
                      {formatDate(document.date_creation)}
                    </span>
                    {document.matrice_details && (
                      <span className="document-matrice">
                        {t('documentHistory.matrice') || 'Matrice'}: {document.matrice_details.langue?.toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="document-preview">
                  <p>{getPreview(document.contenu)}</p>
                </div>
                
                {/* Menu kebab (trois points) */}
                <div className="document-menu" ref={openMenuId === document.id ? menuRef : null}>
                  <button
                    className="document-menu-button"
                    onClick={() => toggleMenu(document.id)}
                    title="Menu"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="1"/>
                      <circle cx="5" cy="12" r="1"/>
                      <circle cx="19" cy="12" r="1"/>
                    </svg>
                  </button>
                  
                  {openMenuId === document.id && (
                    <div className="document-menu-dropdown">
                      <button
                        className="document-menu-option document-menu-edit"
                        onClick={() => handleEdit(document.id)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        <span>{t('documentHistory.edit') || 'Modifier'}</span>
                      </button>
                      <button
                        className="document-menu-option document-menu-delete"
                        onClick={() => handleDelete(document.id)}
                        disabled={deleting === document.id}
                      >
                        {deleting === document.id ? (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="spinner">
                              <circle cx="12" cy="12" r="10"/>
                            </svg>
                            <span>{t('documentHistory.deleting') || 'Suppression...'}</span>
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              <line x1="10" y1="11" x2="10" y2="17"/>
                              <line x1="14" y1="11" x2="14" y2="17"/>
                            </svg>
                            <span>{t('documentHistory.delete') || 'Supprimer'}</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentHistory;

